import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import type { MetaPayload, ResultCandidate, ResultPayload } from "../api/types"
import { Button } from "../components/ui/button"
import { Card } from "../components/ui/card"
import { Spinner } from "../components/ui/spinner"
import { StatusPill } from "../components/ui/status-pill"
import { useToast } from "../components/ui/toast"
import { ApiError } from "../lib/api"
import { cn } from "../lib/cn"
import {
  type EvaluationRating,
  type EvaluationRecord,
  type EvaluationSnapshot,
  useCreateEvaluation,
  useCreateShare,
  useDeleteEvaluation,
  useDeleteRun,
  useRerunMutation,
  useRevokeShare,
  useRunEvaluations,
  useRunMeta,
  useRunResult,
  useUpdateEvaluation,
} from "../lib/hooks"
import { useRunEvents } from "../lib/sse"
import { isSafeHttpUrl } from "../lib/url"

function fmtTime(sec: number): string {
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = Math.floor(sec % 60)
  return h > 0
    ? `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`
    : `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`
}

function fmtDuration(sec: number | null | undefined): string {
  if (sec == null) return "—"
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = Math.floor(sec % 60)
  // ショート切り抜き対象は sub-minute も普通にあるため、分未満は秒で表示する。
  if (h > 0) return `${h}h ${m.toString().padStart(2, "0")}m`
  if (m > 0) return `${m}m ${s.toString().padStart(2, "0")}s`
  return `${s}s`
}

function metaStatus(m: MetaPayload | undefined): string | undefined {
  if (!m) return undefined
  return m.run?.status ?? m.status
}

function resultStatus(r: ResultPayload | undefined): string | undefined {
  return r?.run?.status
}

export function RunDetail() {
  const { runId } = useParams<{ runId: string }>()
  const nav = useNavigate()
  const toast = useToast()
  const qc = useQueryClient()
  const result = useRunResult(runId)
  const meta = useRunMeta(runId)
  // status は meta があれば優先 (live で更新)、無ければ result から
  const status = metaStatus(meta.data) ?? resultStatus(result.data)
  const isLive = status === "running" || status === "queued"
  const events = useRunEvents(isLive ? runId : undefined)
  const rerun = useRerunMutation()
  const del = useDeleteRun()

  // SSE で `run.completed` / `run.failed` が来たら result.json と meta.json を refetch。
  // Phase 4.2 round 2 レビュー: noop_extract は 3 秒で完了するが、実 pipeline (Phase
  // 4-followup) は分単位なので「完了通知 → fresh データ取得」を確実に実行する。
  //
  // Round 3 codex 指摘: event_id は run ごとに 1 から始まるため、runId 変更時に
  // lastSeenEventIdRef を null リセットしないと、rerun 直後の `run.completed`
  // (event_id 1 など) が前 run の高い event_id と比較されて取りこぼされる。
  const lastSeenEventIdRef = useRef<number | null>(null)
  const prevRunIdRef = useRef<string | undefined>(runId)
  // Phase 5 A.3: cost 80% / completion toast は同一 run で 1 回だけ。SSE 再接続で
  // 二重通知されないよう ref で管理 (LocalStorage は run 復旧時に発火する設計だが、
  // ref で十分 + シンプル)。
  const costWarnedRef = useRef(false)
  const completionToastedRef = useRef(false)
  useEffect(() => {
    // runId が変わったら lastSeenEventId / toast 状態をリセット
    if (prevRunIdRef.current !== runId) {
      lastSeenEventIdRef.current = null
      prevRunIdRef.current = runId
      costWarnedRef.current = false
      completionToastedRef.current = false
    }
    if (!runId || events.events.length === 0) return
    for (const e of events.events) {
      if (lastSeenEventIdRef.current !== null && e.event_id <= lastSeenEventIdRef.current) {
        continue
      }
      lastSeenEventIdRef.current = e.event_id
      if (e.event_type === "run.completed" || e.event_type === "run.failed") {
        qc.invalidateQueries({ queryKey: ["runs", runId] })
        // Phase 5 A.3: 完了 toast (1 回だけ)。実 status は payload.status を見る。
        if (!completionToastedRef.current) {
          completionToastedRef.current = true
          const status = (e.payload as { status?: string } | undefined)?.status
          const cancelled = (e.payload as { cancelled?: boolean } | undefined)?.cancelled
          if (cancelled) {
            toast.show({ title: "キャンセルしました", variant: "warning" })
          } else if (status === "success") {
            toast.show({ title: "完了しました", variant: "success" })
          } else if (status === "partial") {
            toast.show({
              title: "一部完了 (partial)",
              description: "warnings をご確認ください",
              variant: "warning",
            })
          } else if (status === "cost_exceeded") {
            toast.show({
              title: "コスト上限に達しました",
              description: "cost_budget_usd の見積もり超過",
              variant: "error",
            })
          } else {
            toast.show({ title: "失敗しました", description: status, variant: "error" })
          }
        }
      }
    }
  }, [events.events, runId, qc, toast])

  // Phase 5 A.3 + Copilot 指摘 C-6: cost 80% warning toast (1 回だけ)。
  // budget は SSE cost.update event 経由 (実行中、優先) → result.usage.llm.cost_budget_usd (完了後)
  // の順で取る。旧実装は result.json のみ参照で実行中に発火しない bug だった。
  const costBudget =
    events.latestCost?.cost_budget_usd ?? result.data?.usage?.llm?.cost_budget_usd ?? null
  useEffect(() => {
    if (!events.latestCost || !costBudget || costBudget <= 0) return
    if (costWarnedRef.current) return
    const ratio = events.latestCost.cumulative_cost_usd / costBudget
    if (ratio >= 0.8) {
      costWarnedRef.current = true
      toast.show({
        title: `LLM コストが 80% に達しました (${(ratio * 100).toFixed(0)}%)`,
        description: `累積 ${events.latestCost.cumulative_cost_usd.toFixed(4)} / 上限 ${costBudget.toFixed(2)} USD`,
        variant: "warning",
      })
    }
  }, [events.latestCost, costBudget, toast])

  if (result.isLoading && meta.isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner className="size-6" />
      </div>
    )
  }

  // result が 404 だけだと「実行中で result.json 未生成」と区別できないので、
  // meta も 404 のときだけ "run が存在しない" と判定する。
  // TanStack Query は cached `data` を保持したまま refetch error を抱える状態に
  // なり得る (staleTime: 10_000)。stale cache のまま 404 を見逃さないよう、
  // 主条件は data 有無ではなく error.status === 404 にする。
  const resultNotFound = result.error instanceof ApiError && result.error.status === 404
  const metaNotFound = meta.error instanceof ApiError && meta.error.status === 404
  if (resultNotFound && metaNotFound) {
    return (
      <div className="px-20 pt-10">
        <Card className="p-6 text-sm text-danger">
          run が見つかりません ({runId})。削除済みか URL が間違っている可能性があります。
        </Card>
      </div>
    )
  }

  // Phase 5 A.5: ConfigError UI fallback。result.json は無いが meta があるケース
  // (metadata fetch 失敗 / input.max_duration 超過 / 設定エラー等)。meta.errors を表示する。
  // Copilot r2 R-6: cancelled は失敗ではないので文言を分岐 (中立表現に)。
  if (resultNotFound && meta.data && status !== "running" && status !== "queued") {
    const metaErrors = (meta.data.errors as string[] | undefined) ?? []
    const metaWarnings = (meta.data.warnings as string[] | undefined) ?? []
    const isCancelled = status === "cancelled"
    const headlineClass = isCancelled
      ? "border-warning/40 bg-warning/10 p-6"
      : "border-danger/40 bg-danger/10 p-6"
    const titleClass = isCancelled
      ? "mb-3 text-base font-semibold text-warning"
      : "mb-3 text-base font-semibold text-danger"
    const headline = isCancelled
      ? "Run はキャンセルされました (result.json 未生成)"
      : "Run は失敗しました (result.json 未生成)"
    return (
      <div className="flex flex-col gap-6 px-20 pt-10">
        <Card className={headlineClass}>
          <div className={titleClass}>{headline}</div>
          <div className="mb-1 text-xs text-text-muted">status: {status ?? "unknown"}</div>
          {metaErrors.length > 0 ? (
            <>
              <div className="mt-3 mb-1 text-xs font-semibold text-danger">エラー</div>
              <ul className="list-disc space-y-1 pl-5 text-xs text-text-secondary">
                {metaErrors.map((e, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: 同 message 重複あり得る
                  <li key={`meta-err-${i}-${e}`}>{e}</li>
                ))}
              </ul>
            </>
          ) : (
            <div className="mt-3 text-xs text-text-secondary">
              エラー詳細無し (meta.errors 空)。run.log をご確認ください。
            </div>
          )}
          {metaWarnings.length > 0 ? (
            <>
              <div className="mt-3 mb-1 text-xs font-semibold text-warning">警告</div>
              <ul className="list-disc space-y-1 pl-5 text-xs text-text-secondary">
                {metaWarnings.map((w, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: 同 message 重複あり得る
                  <li key={`meta-warn-${i}-${w}`}>{w}</li>
                ))}
              </ul>
            </>
          ) : null}
        </Card>
      </div>
    )
  }
  if (!result.data && !resultNotFound && result.error) {
    return (
      <div className="px-20 pt-10">
        <Card className="p-6 text-sm text-danger">読み込みに失敗しました</Card>
      </div>
    )
  }

  const handleDownload = () => {
    if (!result.data) return
    const blob = new Blob([JSON.stringify(result.data, null, 2)], {
      type: "application/json",
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `${runId}-result.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleDelete = async () => {
    if (!runId) return
    if (!confirm(`Run ${runId} を削除しますか？\n(実行中の場合はキャンセルされます)`)) return
    try {
      await del.mutateAsync(runId)
      toast.show({ title: "削除しました", variant: "success" })
      nav("/runs", { replace: true })
    } catch (err) {
      // 409 (cancel timeout) は run がまだ走っている可能性が高い。少し待って再操作を案内。
      const apiErr = err instanceof ApiError ? err : null
      toast.show({
        title: apiErr?.status === 409 ? "実行中のためまだ削除できません" : "削除できませんでした",
        description: err instanceof Error ? err.message : undefined,
        variant: "error",
      })
    }
  }

  const handleRerun = async () => {
    if (!runId) return
    try {
      const resp = await rerun.mutateAsync(runId)
      toast.show({ title: "再実行を開始しました", description: resp.run_id, variant: "success" })
      nav(`/runs/${resp.run_id}`)
    } catch (err) {
      toast.show({
        title: "再実行できませんでした",
        description: err instanceof Error ? err.message : undefined,
        variant: "error",
      })
    }
  }

  return (
    <div className="flex flex-col gap-6 px-20 pb-20 pt-7">
      <div className="flex items-center gap-2 text-xs">
        <Link to="/runs" className="font-medium text-text-muted hover:text-text-secondary">
          Runs
        </Link>
        <span className="text-text-muted">›</span>
        <span className="text-text-secondary">{runId}</span>
      </div>

      <RunSummary
        runId={runId ?? ""}
        result={result.data}
        status={status}
        onDownload={handleDownload}
        onDelete={handleDelete}
        onRerun={handleRerun}
        canDownload={!!result.data}
        shareToken={meta.data?.share_token ?? null}
      />

      {isLive ? (
        <Card className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Spinner className="size-4" />
              <span className="text-sm text-text-secondary">
                実行中… ({events.events.length} イベント /{" "}
                {events.connected ? "接続中" : "再接続中"})
              </span>
            </div>
            {events.events.length > 0 ? (
              <span className="text-xs text-text-muted">
                latest: {events.events[events.events.length - 1].event_type}
              </span>
            ) : null}
          </div>
        </Card>
      ) : null}

      {result.data ? (
        <>
          {/* Phase 5 A.4: live warnings/errors (SSE) と result.warnings/errors を merge。
              重複 (同 message) は dedup。実行中は live のみ、完了後は result が authoritative。 */}
          {(() => {
            const resultErrors = result.data.errors ?? []
            const resultWarnings = result.data.warnings ?? []
            // dedup: result + live を union (重複除去)
            const mergedErrors = Array.from(new Set([...resultErrors, ...events.liveErrors]))
            const mergedWarnings = Array.from(new Set([...resultWarnings, ...events.liveWarnings]))
            return (
              <>
                {mergedErrors.length > 0 ? (
                  <Card className="border-danger/40 bg-danger/10 p-4">
                    <div className="mb-2 text-sm font-semibold text-danger">エラー</div>
                    <ul className="list-disc space-y-1 pl-5 text-xs text-text-secondary">
                      {mergedErrors.map((e, i) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: 同 message 重複あり得る (e.g. 同 proposer の per-chunk warning) → index で区別
                        <li key={`err-${i}-${e}`}>{e}</li>
                      ))}
                    </ul>
                  </Card>
                ) : null}
                {mergedWarnings.length > 0 ? (
                  <Card className="border-warning/40 bg-warning/10 p-4">
                    <div className="mb-2 text-sm font-semibold text-warning">警告</div>
                    <ul className="list-disc space-y-1 pl-5 text-xs text-text-secondary">
                      {mergedWarnings.map((w, i) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: 同 message 重複あり得る
                        <li key={`warn-${i}-${w}`}>{w}</li>
                      ))}
                    </ul>
                  </Card>
                ) : null}
              </>
            )
          })()}
          <Timeline
            duration={result.data.source.duration_sec}
            candidates={result.data.candidates}
          />

          <div className="flex items-center justify-between">
            <div className="flex items-baseline gap-2.5">
              <h2 className="text-lg font-semibold text-text-primary">
                候補 ({result.data.candidates.length})
              </h2>
              <span className="text-xs text-text-muted">
                primary: {result.data.candidates[0]?.primary_scorer ?? "—"}
              </span>
            </div>
          </div>

          {result.data.candidates.length === 0 ? (
            <Card className="p-12 text-center text-sm text-text-secondary">候補がありません</Card>
          ) : (
            <CandidateList
              result={result.data}
              runId={runId ?? ""}
              allProposers={extractEnabledProposers(result.data?.run.config_summary)}
            />
          )}
        </>
      ) : (
        <Card className="p-12 text-center text-sm text-text-secondary">
          {isLive ? "結果待ち… (実行中)" : "結果がまだありません"}
        </Card>
      )}
    </div>
  )
}

function RunSummary({
  runId,
  result,
  status,
  onDownload,
  onDelete,
  onRerun,
  canDownload,
  shareToken,
}: {
  runId: string
  result: ResultPayload | undefined
  status: string | undefined
  onDownload: () => void
  onDelete: () => void
  onRerun: () => void
  canDownload: boolean
  shareToken: string | null
}) {
  const src = result?.source
  const cfg = result?.run.config_summary ?? {}
  const focusObj = cfg.focus as { preset?: string } | undefined
  const focus = focusObj?.preset ?? (cfg.focus_preset as string | undefined) ?? "auto"
  const proposersObj = cfg.proposers as { transcript?: { model?: string } } | undefined
  const model = proposersObj?.transcript?.model ?? (cfg.llm_model as string | undefined) ?? "—"
  const subLang = src?.language ?? "—"
  const llm = result?.usage?.llm
  const cost = llm?.total_estimated_cost_usd
  const costBudget = llm?.cost_budget_usd
  const isShared = !!shareToken

  return (
    <Card className="flex flex-col gap-4 p-6">
      <div className="flex items-center gap-6">
        <div className="h-[90px] w-40 shrink-0 rounded-lg bg-surface-2" />
        <div className="flex flex-1 flex-col gap-2.5">
          <div className="flex items-center gap-3">
            <StatusPill state={status} />
            <h1 className="text-xl font-semibold text-text-primary">{src?.title ?? runId}</h1>
          </div>
          {src ? (
            <div className="flex items-center gap-2.5 text-xs">
              {src.channel ? (
                <span className="font-medium text-text-secondary">{src.channel}</span>
              ) : null}
              {src.channel ? <span className="text-text-muted">·</span> : null}
              {isSafeHttpUrl(src.url) ? (
                <a
                  href={src.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-info hover:underline"
                >
                  {src.url}
                </a>
              ) : (
                <span className="text-text-muted">{src.url}</span>
              )}
            </div>
          ) : null}
          <div className="flex flex-wrap gap-8 pt-1">
            <Meta label="配信長" value={fmtDuration(src?.duration_sec)} />
            <Meta label="候補数" value={String(result?.candidates.length ?? 0)} />
            <Meta label="言語" value={String(subLang)} />
            <Meta label="Focus" value={focus} />
            <Meta label="モデル" value={model} />
            <Meta
              label="実コスト"
              value={
                cost != null
                  ? `$${cost.toFixed(2)}${costBudget != null ? ` / $${costBudget.toFixed(2)}` : ""}`
                  : "—"
              }
            />
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Button variant="outline" size="sm" onClick={onDownload} disabled={!canDownload}>
            ⤓ JSON ダウンロード
          </Button>
          <Button variant="outline" size="sm" onClick={onRerun}>
            ⟳ 再実行
          </Button>
          <ShareButton runId={runId} isShared={isShared} />
          <Button variant="danger" size="sm" onClick={onDelete}>
            ✕ 削除
          </Button>
        </div>
      </div>
      {isShared ? <SharePanel runId={runId} shareToken={shareToken} /> : null}
    </Card>
  )
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] text-text-muted">{label}</span>
      <span className="text-[13px] font-medium text-text-primary">{value}</span>
    </div>
  )
}

function Timeline({
  duration,
  candidates,
}: {
  duration: number | null
  candidates: ResultCandidate[]
}) {
  if (candidates.length === 0 && (duration == null || duration === 0)) {
    return null
  }
  const total = duration ?? Math.max(1, ...candidates.map((c) => c.end_sec))
  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-medium text-text-muted">タイムライン（候補位置）</span>
      <Card className="p-4">
        <div className="relative h-3.5 w-full rounded-md bg-surface-2">
          {candidates.map((c) => (
            <div
              key={`${c.start_sec}-${c.end_sec}`}
              className="absolute top-0 h-full rounded-sm bg-accent opacity-70 hover:opacity-100"
              style={{
                left: `${(c.start_sec / total) * 100}%`,
                width: `${Math.max(0.3, ((c.end_sec - c.start_sec) / total) * 100)}%`,
              }}
              title={`${fmtTime(c.start_sec)} → ${fmtTime(c.end_sec)}`}
            />
          ))}
        </div>
        <div className="mt-2 flex justify-between text-[11px] text-text-muted">
          <span>{fmtTime(0)}</span>
          <span>{fmtTime(total / 4)}</span>
          <span>{fmtTime(total / 2)}</span>
          <span>{fmtTime((total * 3) / 4)}</span>
          <span>{fmtTime(total)}</span>
        </div>
      </Card>
    </div>
  )
}

// LlmScorer の text/chat/audio intensity を表示する小バッジ。値の強さで色が変わる。
function IntensityBadge({ label, value }: { label: string; value: number }) {
  // 0-1 の値を 3 段階で色分け (>=0.6 緑、>=0.3 黄、<0.3 グレー)。視認性のためのみ
  const bgClass =
    value >= 0.6
      ? "bg-success/20 text-success"
      : value >= 0.3
        ? "bg-warning/20 text-warning"
        : "bg-text-muted/20 text-text-muted"
  return (
    <span className={cn("rounded px-1.5 py-0.5 font-medium", bgClass)}>
      {label} {value.toFixed(2)}
    </span>
  )
}

// config_summary.proposers は extract_runner.py で enabled proposer 名の list[str]。
// 古い run / 不正な構造でも壊さないよう defensively に扱う。
function extractEnabledProposers(configSummary: Record<string, unknown> | undefined): string[] {
  if (!configSummary) return []
  const v = configSummary.proposers
  if (Array.isArray(v)) return v.filter((x): x is string => typeof x === "string")
  return []
}

function CandidateList({
  result,
  runId,
  allProposers,
}: {
  result: ResultPayload
  runId: string
  allProposers: string[]
}) {
  // Phase 5 B Frontend: run の current view を取得して、candidate_id をキーに既評価をひもづける
  const evaluations = useRunEvaluations(runId)
  const evalByCandidate = new Map<string, EvaluationRecord>()
  for (const e of evaluations.data ?? []) {
    evalByCandidate.set(e.candidate_id, e)
  }

  // config_summary.focus は string | { preset, custom_prompt }。snapshot に focus を入れたい。
  const focusRaw = result.run.config_summary.focus
  const focus =
    typeof focusRaw === "string"
      ? focusRaw
      : ((focusRaw as { preset?: string } | undefined)?.preset ?? null)

  return (
    <div className="flex flex-col gap-3.5">
      {result.candidates.map((c, idx) => {
        const candidateId = `${runId}_rank${c.rank}`
        const snapshot = buildEvaluationSnapshot(c, result, focus)
        return (
          <CandidateCard
            // Copilot r4 R4-2: candidateId は ${runId}_rank${rank} で run 横断で一意。
            // start/end/rank だけだと別 run の同 rank 候補と key 衝突して React が
            // コンポーネント再利用 → 評価 UI state (コメント開閉 / 入力中の値) が前 run のまま残る。
            key={candidateId}
            candidate={c}
            candidateIndex={idx}
            candidateId={candidateId}
            runId={runId}
            sourceUrl={result.source.url ?? ""}
            allProposers={allProposers}
            existing={evalByCandidate.get(candidateId) ?? null}
            snapshot={snapshot}
          />
        )
      })}
    </div>
  )
}

function buildEvaluationSnapshot(
  c: ResultCandidate,
  result: ResultPayload,
  focus: string | null,
): EvaluationSnapshot {
  const primaryScoreEntry = c.scores?.[c.primary_scorer]
  const primaryMeta = (primaryScoreEntry?.metadata ?? {}) as Record<string, unknown>
  const intensity: Record<string, number | null> = {
    text: typeof primaryMeta.text_intensity === "number" ? primaryMeta.text_intensity : null,
    chat: typeof primaryMeta.chat_intensity === "number" ? primaryMeta.chat_intensity : null,
    audio: typeof primaryMeta.audio_intensity === "number" ? primaryMeta.audio_intensity : null,
  }
  const llmModel = typeof primaryMeta.llm_model === "string" ? primaryMeta.llm_model : null
  return {
    start_sec: c.start_sec,
    end_sec: c.end_sec,
    primary_score: c.primary_score,
    primary_scorer: c.primary_scorer,
    intensity,
    contributors: c.contributors.map((ct) => ({
      proposer: ct.proposer,
      confidence: ct.confidence,
      reason: ct.reason,
    })),
    focus,
    model: llmModel,
    source_url: result.source.url ?? null,
    video_title: result.source.title ?? null,
  }
}

const RATING_LABELS: Record<EvaluationRating, string> = {
  good: "いい",
  neutral: "ふつう",
  bad: "微妙",
}
// user 確定 2026-06-11: neutral 絵文字を 🤔 (thinking) → 😐 (face neutral) に変更。
// 「考え中」ではなく「中立 / どちらでもない」を直接表す絵文字に統一。
const RATING_EMOJI: Record<EvaluationRating, string> = {
  good: "👍",
  neutral: "😐",
  bad: "👎",
}

function CandidateCard({
  candidate,
  candidateIndex,
  candidateId,
  runId,
  sourceUrl,
  allProposers,
  existing,
  snapshot,
}: {
  candidate: ResultCandidate
  candidateIndex: number
  candidateId: string
  runId: string
  sourceUrl: string
  allProposers: string[]
  existing: EvaluationRecord | null
  snapshot: EvaluationSnapshot
}) {
  const rawJumpUrl =
    candidate.youtube_url_with_t ??
    (sourceUrl.includes("youtube.com/watch")
      ? `${sourceUrl}&t=${Math.floor(candidate.start_sec)}s`
      : sourceUrl)
  // backend は CreateRunRequest.url の scheme を検証していない。`javascript:` 等が
  // 入っていても storage に残るので、href として出す前に http(s) のみ許可する。
  const youtubeUrl = isSafeHttpUrl(rawJumpUrl) ? rawJumpUrl : null
  const scoreEntries = Object.entries(candidate.scores ?? {})

  // contributors を proposer 名でグループ化。allProposers をループして「提案あり / なし」
  // を均等に並べる (= Candidate ごとに常に同じ行数になる)。proposer 由来でない突発的な
  // contributor (extra) があれば末尾に並べる。
  const contribByProposer = new Map<string, typeof candidate.contributors>()
  for (const c of candidate.contributors) {
    const arr = contribByProposer.get(c.proposer) ?? []
    arr.push(c)
    contribByProposer.set(c.proposer, arr)
  }
  const proposerOrder =
    allProposers.length > 0 ? allProposers : Array.from(contribByProposer.keys())
  const extraProposers = Array.from(contribByProposer.keys()).filter(
    (n) => !proposerOrder.includes(n),
  )
  const allRows = [...proposerOrder, ...extraProposers]

  return (
    <Card className="flex items-start gap-6 p-5">
      <div className="flex flex-col items-center gap-2">
        <span className="text-2xl font-semibold text-text-primary">#{candidate.rank}</span>
        <span className="rounded-md bg-accent px-2.5 py-1 text-sm font-semibold text-text-on-accent">
          {candidate.primary_score.toFixed(2)}
        </span>
      </div>
      <div className="flex flex-1 flex-col gap-3">
        <div className="flex items-center gap-3">
          <span className="text-[15px] font-semibold text-text-primary">
            {fmtTime(candidate.start_sec)} → {fmtTime(candidate.end_sec)}
          </span>
          <span className="text-[13px] font-medium text-text-muted">
            ({candidate.duration_sec.toFixed(1)}s)
          </span>
        </div>
        {candidate.preview_text ? (
          <p className="text-[13px] leading-5 text-text-secondary">{candidate.preview_text}</p>
        ) : null}

        {scoreEntries.length > 0 ? (
          <div className="flex flex-col gap-1.5">
            <span className="text-[11px] font-medium uppercase tracking-wide text-text-muted">
              scores
            </span>
            <div className="flex flex-col gap-1.5">
              {scoreEntries.map(([name, s]) => {
                const meta = (s.metadata ?? {}) as Record<string, unknown>
                const textInt = typeof meta.text_intensity === "number" ? meta.text_intensity : null
                const chatInt = typeof meta.chat_intensity === "number" ? meta.chat_intensity : null
                const audioInt =
                  typeof meta.audio_intensity === "number" ? meta.audio_intensity : null
                const agreement =
                  typeof meta.agreement_strength === "string" ? meta.agreement_strength : null
                const hasIntensities = textInt !== null || chatInt !== null || audioInt !== null
                const agreementColor =
                  agreement === "strong"
                    ? "bg-success/20 text-success"
                    : agreement === "moderate"
                      ? "bg-warning/20 text-warning"
                      : agreement === "weak"
                        ? "bg-text-muted/20 text-text-muted"
                        : ""
                return (
                  <div
                    key={name}
                    className={cn(
                      "rounded-md bg-surface-2 px-3 py-2",
                      name === candidate.primary_scorer
                        ? "border border-accent"
                        : "border border-transparent",
                      s.status === "skipped" || s.status === "failed" ? "opacity-60" : "",
                    )}
                  >
                    <div className="flex items-center gap-2 text-[12px]">
                      <span className="font-medium text-text-secondary">{name}</span>
                      <span className="font-semibold text-text-primary">
                        {s.value != null ? s.value.toFixed(2) : "—"}
                      </span>
                      {agreement ? (
                        <span
                          className={cn(
                            "rounded px-1.5 py-0.5 text-[10px] font-medium",
                            agreementColor,
                          )}
                        >
                          {agreement}
                        </span>
                      ) : null}
                      {s.status !== "success" ? (
                        <span className="text-[11px] text-text-muted">·{s.status}</span>
                      ) : null}
                    </div>
                    {hasIntensities ? (
                      <div className="mt-1.5 flex flex-wrap gap-1.5 text-[10px]">
                        {textInt !== null ? <IntensityBadge label="字幕" value={textInt} /> : null}
                        {chatInt !== null ? (
                          <IntensityBadge label="チャット" value={chatInt} />
                        ) : null}
                        {audioInt !== null ? (
                          <IntensityBadge label="音声" value={audioInt} />
                        ) : null}
                      </div>
                    ) : null}
                    {s.reason ? (
                      <p className="mt-1 text-[11px] leading-4 text-text-muted">{s.reason}</p>
                    ) : null}
                  </div>
                )
              })}
            </div>
          </div>
        ) : null}

        <div className="flex flex-col gap-1.5">
          <span className="text-[11px] font-medium uppercase tracking-wide text-text-muted">
            contributors
          </span>
          <div className="flex flex-col gap-1.5">
            {allRows.map((proposerName) => {
              const contribs = contribByProposer.get(proposerName) ?? []
              if (contribs.length === 0) {
                return (
                  <div
                    key={proposerName}
                    className="rounded-md border border-dashed border-border-subtle px-3 py-2 text-[11px] text-text-muted"
                  >
                    <span className="font-medium">{proposerName}</span>
                    <span className="ml-2">— 提案なし</span>
                  </div>
                )
              }
              // 同じ proposer (例: chat_llm) から複数の Proposal が IoU で同じ Candidate に
              // merge されて contributors に並ぶケースがある (ChatLlmProposer は window
              // 30s × step 10s なので隣接 window で重複検出されやすい)。
              // UI は「1 proposer = 1 行」に統一するため、最高 confidence の 1 件だけを
              // 表示する。他 contribute は result.json で見られる。
              const best = contribs.reduce((a, b) => (a.confidence >= b.confidence ? a : b))
              const quote = (best.metadata as Record<string, unknown> | undefined)?.quote
              const quoteText = typeof quote === "string" ? quote : null
              const extraCount = contribs.length - 1
              return (
                <div key={best.proposal_id} className="rounded-md bg-surface-2 px-3 py-2">
                  <div className="flex items-center gap-2 text-[12px]">
                    <span className="font-medium text-text-secondary">{best.proposer}</span>
                    <span className="font-semibold text-text-primary">
                      {best.confidence.toFixed(2)}
                    </span>
                    {extraCount > 0 ? (
                      <span
                        className="text-[10px] text-text-muted"
                        title={`同 proposer から他 ${extraCount} 件 (隣接 window 重複)`}
                      >
                        +{extraCount}
                      </span>
                    ) : null}
                  </div>
                  {best.reason ? (
                    <p className="mt-1 text-[11px] leading-4 text-text-muted">{best.reason}</p>
                  ) : null}
                  {quoteText ? (
                    <p className="mt-1 text-[11px] leading-4 text-text-muted">
                      <span className="text-text-muted">字幕:</span>「{quoteText}」
                    </p>
                  ) : null}
                </div>
              )
            })}
          </div>
        </div>
      </div>
      <div className="flex flex-col items-end gap-2">
        {youtubeUrl ? (
          <a
            href={youtubeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center rounded-lg border border-border-default px-3.5 py-2 text-xs font-medium text-text-primary hover:bg-surface-2"
          >
            ▷ YouTube で開く
          </a>
        ) : null}
        <Link
          to={`/runs/${encodeURIComponent(runId)}/clips/${candidate.rank}`}
          className="inline-flex items-center rounded-lg border border-accent px-3.5 py-2 text-xs font-semibold text-accent hover:bg-accent/10"
        >
          ⤴ クリップ生成
        </Link>
        {/* Phase 5 B Frontend: 評価ボタン + コメント */}
        <EvaluationControls
          runId={runId}
          candidateId={candidateId}
          candidateIndex={candidateIndex}
          existing={existing}
          snapshot={snapshot}
        />
      </div>
    </Card>
  )
}

function EvaluationControls({
  runId,
  candidateId,
  candidateIndex,
  existing,
  snapshot,
}: {
  runId: string
  candidateId: string
  candidateIndex: number
  existing: EvaluationRecord | null
  snapshot: EvaluationSnapshot
}) {
  const toast = useToast()
  const create = useCreateEvaluation(runId)
  const update = useUpdateEvaluation(runId)
  const remove = useDeleteEvaluation(runId)
  const [showComment, setShowComment] = useState(false)
  const [comment, setComment] = useState(existing?.free_comment ?? "")

  // Copilot r4 R4-3: 旧 deps=[existing] だと TanStack の background refetch で
  // existing が同内容別オブジェクトとして返るたびに setComment され、ユーザの
  // 未保存入力が消える bug。existingComment 値が変わった時だけ同期する。
  // biome useExhaustiveDependencies: setComment は React 保証で安定なので dep 不要、
  // effect body は existingComment のみ参照するため deps はそれ 1 つ。
  const existingComment = existing?.free_comment ?? ""
  useEffect(() => {
    setComment(existingComment)
  }, [existingComment])

  const currentRating = existing?.rating ?? null
  const isPending = create.isPending || update.isPending || remove.isPending

  const handleRating = async (rating: EvaluationRating) => {
    if (isPending) return
    try {
      if (!existing) {
        // 新規 (rating_created)
        await create.mutateAsync({
          run_id: runId,
          candidate_id: candidateId,
          candidate_index: candidateIndex,
          rating,
          free_comment: comment || null,
          snapshot,
        })
      } else if (existing.rating === rating) {
        // 同じボタン押下 → 取り消し (rating_deleted)
        await remove.mutateAsync(existing.evaluation_id)
      } else {
        // 別ボタン押下 → 更新 (rating_updated)
        await update.mutateAsync({
          evaluation_id: existing.evaluation_id,
          body: { rating },
        })
      }
    } catch (err) {
      toast.show({
        title: "評価の保存に失敗しました",
        description: err instanceof Error ? err.message : undefined,
        variant: "error",
      })
    }
  }

  const handleSaveComment = async () => {
    if (isPending) return
    try {
      if (!existing) {
        // コメントだけ保存できないので「rating + comment 同時」を前提に、まず rating を選んでもらう
        toast.show({
          title: "先に評価を選択してください",
          description: "コメントは評価と一緒に保存されます",
          variant: "warning",
        })
        return
      }
      await update.mutateAsync({
        evaluation_id: existing.evaluation_id,
        body: { free_comment: comment || null },
      })
      toast.show({ title: "コメントを保存しました", variant: "success" })
      setShowComment(false)
    } catch (err) {
      toast.show({
        title: "コメント保存に失敗しました",
        description: err instanceof Error ? err.message : undefined,
        variant: "error",
      })
    }
  }

  return (
    <div className="flex flex-col items-end gap-1.5">
      <div className="flex flex-col gap-1">
        {(["good", "neutral", "bad"] as const).map((r) => {
          const selected = currentRating === r
          return (
            <button
              key={r}
              type="button"
              disabled={isPending}
              onClick={() => handleRating(r)}
              className={cn(
                "inline-flex items-center justify-center rounded-lg px-2.5 py-1.5 text-base transition-colors",
                selected
                  ? "bg-accent text-text-on-accent"
                  : "border border-border-default hover:bg-surface-2",
                isPending && "cursor-wait opacity-60",
              )}
              title={
                selected
                  ? `${RATING_LABELS[r]} (もう一度押すと取り消し)`
                  : `評価: ${RATING_LABELS[r]}`
              }
              aria-label={`評価 ${RATING_LABELS[r]}`}
            >
              {RATING_EMOJI[r]}
            </button>
          )
        })}
      </div>
      <button
        type="button"
        onClick={() => setShowComment((v) => !v)}
        className="text-[11px] text-text-muted hover:text-text-secondary"
      >
        {showComment ? "▲ 閉じる" : `💬 コメント${existing?.free_comment ? " (あり)" : ""}`}
      </button>
      {showComment ? (
        <div className="flex w-64 flex-col gap-1">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="評価コメント (あれば)"
            rows={3}
            className="w-full rounded-md border border-border-default bg-surface-2 px-2 py-1.5 text-[11px] text-text-primary"
          />
          <button
            type="button"
            onClick={handleSaveComment}
            disabled={isPending || !existing}
            className={cn(
              "self-end rounded-md bg-accent px-2.5 py-1 text-[11px] font-medium text-text-on-accent",
              (isPending || !existing) && "cursor-not-allowed opacity-60",
            )}
            title={!existing ? "先に評価を選択してください" : undefined}
          >
            保存
          </button>
        </div>
      ) : null}
    </div>
  )
}

/**
 * 右側アクション列に置く「共有」ボタン。未共有 = create、共有中 = URL を toast 表示してコピー。
 * 共有中のとき「解除」操作は SharePanel (Card 内 inline) 側に置く。
 */
function ShareButton({ runId, isShared }: { runId: string; isShared: boolean }) {
  const toast = useToast()
  const create = useCreateShare()

  const handleShare = async () => {
    if (isShared) {
      // 既に共有中なら何もしない (UI 上は SharePanel で URL + 解除 が見える)
      return
    }
    try {
      const resp = await create.mutateAsync(runId)
      const url = `${window.location.origin}${resp.share_path}`
      try {
        await navigator.clipboard.writeText(url)
        toast.show({
          title: "共有 URL を作成 + コピーしました",
          description: url,
          variant: "success",
        })
      } catch {
        toast.show({ title: "共有 URL を作成しました", description: url, variant: "success" })
      }
    } catch (err) {
      toast.show({
        title: "共有に失敗しました",
        description: err instanceof Error ? err.message : undefined,
        variant: "error",
      })
    }
  }

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleShare}
      disabled={create.isPending || isShared}
      title={isShared ? "下の共有 URL パネルで操作できます" : "リンク共有 (評価データは非公開)"}
    >
      {isShared ? "🔗 共有中" : "🔗 共有"}
    </Button>
  )
}

/**
 * 共有中のとき RunSummary Card 内に inline で出す URL + コピー + 解除 panel。
 */
function SharePanel({ runId, shareToken }: { runId: string; shareToken: string }) {
  const toast = useToast()
  const revoke = useRevokeShare()
  const shareUrl = `${window.location.origin}/shared/${shareToken}`

  const handleRevoke = async () => {
    if (!window.confirm("共有を解除します。元の URL は使えなくなります。よろしいですか?")) {
      return
    }
    try {
      await revoke.mutateAsync(runId)
      toast.show({ title: "共有を解除しました", variant: "success" })
    } catch (err) {
      toast.show({
        title: "解除に失敗しました",
        description: err instanceof Error ? err.message : undefined,
        variant: "error",
      })
    }
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      toast.show({ title: "URL をコピーしました", variant: "success" })
    } catch {
      toast.show({
        title: "コピーできませんでした",
        description: "URL を選択して手動でコピーしてください",
        variant: "warning",
      })
    }
  }

  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-surface-2 px-3 py-2">
      <span className="shrink-0 text-xs font-medium text-text-secondary">🔗 共有中</span>
      <input
        type="text"
        readOnly
        value={shareUrl}
        className="flex-1 rounded-md border border-border bg-surface px-2 py-1 text-xs text-text-secondary"
        onFocus={(e) => e.currentTarget.select()}
      />
      <Button variant="outline" size="sm" onClick={handleCopy}>
        コピー
      </Button>
      <Button variant="danger" size="sm" onClick={handleRevoke} disabled={revoke.isPending}>
        解除
      </Button>
    </div>
  )
}
